import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, boutiqueMeta } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, X, Search, AlertTriangle, ChevronRight, ChevronDown, Phone, Users, Clock,
  ClipboardCheck, FileSignature, ShieldCheck, GraduationCap, Award, Ban, Circle,
  BarChart3, Sparkles, FileText, Copy, Send, Check, BadgeCheck,
  Binoculars, Handshake, MoreVertical, CircleDot, MapPin, Mail, PhoneCall,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import OfferTemplatesDialog from "@/components/OfferTemplatesDialog";
import { celebrateSigned } from "@/lib/celebrate";
import { SkeletonCard, Skeleton } from "@/components/Skeleton";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import "./hiring-workspace.css";
import "./hiring-directory.css";

const BOUTIQUES = ["PLT", "FLL", "WYN", "PNC"];

const STAGE_EMPTY_MSG = {
  applied: "No new applicants here — a quiet inbox is a good day too.",
  phone_screen: "No one waiting to be screened.",
  gm_interview: "No interviews on deck. The floor is calm.",
  hire_decision: "No decisions pending — GMs are caught up.",
  conditional_offer: "No offers being drafted right now.",
  offer_signed: "No pending offers awaiting signature.",
  background_check: "No background checks in flight.",
  license_cleared: "No licenses to verify.",
  training_scheduled: "No trainings scheduled — yet.",
  ready_to_train: "No one queued for first shift.",
  hired: "Recent hires will land here.",
};

const KANBAN_ORDER = [
  "applied", "phone_screen", "gm_interview", "hire_decision", "conditional_offer",
  "offer_signed", "background_check", "license_cleared", "training_scheduled",
  "ready_to_train", "hired",
];

const STAGE_ICON = {
  applied: Circle,
  phone_screen: Phone,
  gm_interview: Users,
  hire_decision: ClipboardCheck,
  conditional_offer: FileSignature,
  offer_signed: FileSignature,
  background_check: ShieldCheck,
  license_cleared: ShieldCheck,
  training_scheduled: GraduationCap,
  ready_to_train: GraduationCap,
  hired: Award,
  not_hired: Ban,
};

const WORKLIST_STAGES = [
  { id: "discover", label: "Discover", icon: Binoculars, stages: ["applied", "phone_screen"], description: "Initial screening and GM handoff" },
  { id: "assess", label: "Assess", icon: CircleDot, stages: ["gm_interview", "hire_decision"], description: "In-person interview and decision" },
  { id: "offer", label: "Offer", icon: Handshake, stages: ["conditional_offer", "offer_signed", "background_check", "license_cleared"], description: "Offer, clearance, and documentation" },
  { id: "ready", label: "Ready", icon: BadgeCheck, stages: ["training_scheduled", "ready_to_train", "hired"], description: "Training setup and first shift" },
];

const RESPONSIBILITY = {
  applied: { owner: "Sheila", action: "Complete initial screen", evidence: ["Screening notes", "Availability confirmed", "Role expectations confirmed"] },
  phone_screen: { owner: "Sheila", action: "Hand off interview details", evidence: ["Candidate availability", "Screening notes", "GM calendar invite", "Items to validate"] },
  gm_interview: { owner: "General Manager", action: "Complete in-person interview", evidence: ["Interview notes", "Boutique tour", "Availability reconfirmed"] },
  hire_decision: { owner: "General Manager", action: "Record hire decision", evidence: ["Decision", "Decision notes"] },
  conditional_offer: { owner: "General Manager", action: "Present conditional offer", evidence: ["Candidate email confirmed", "Offer terms", "Offer delivered"] },
  offer_signed: { owner: "GM & Sheila", action: "Confirm signed offer", evidence: ["Signed conditional offer", "Copy sent to candidate and KK"] },
  background_check: { owner: "Sheila", action: "Confirm background clearance", evidence: ["Background check cleared", "GM and KK notified"] },
  license_cleared: { owner: "Sheila", action: "Verify MT license", evidence: ["Current MT license", "License clearance"] },
  training_scheduled: { owner: "General Manager", action: "Schedule training", evidence: ["Training calendar invite", "Pay rate and start date", "First training shift"] },
  ready_to_train: { owner: "KK", action: "Complete onboarding", evidence: ["I-9 / E-Verify", "Payroll setup", "Dress code sent"] },
  hired: { owner: "KK", action: "Confirm first shift", evidence: ["Employee record", "First scheduled shift"] },
};

const SEV_STYLE = {
  high: "border-rose-200 bg-rose-50 text-rose-700",
  medium: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function dueLabel(iso) {
  const days = daysSince(iso);
  if (days === null || Number.isNaN(days)) return "—";
  if (days === 0) return "Today";
  return `${days}d in stage`;
}

export function candidateInitials(name){const words=(name||'').trim().split(/\s+/).filter(Boolean);return words.length ? [words[0],...(words.length>1?[words[words.length-1]]:[])].map(word=>Array.from(word)[0]).join('').toLocaleUpperCase() : '?';}

export default function HiringTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLeadership = ["owner", "coo", "recruiter", "onboarding_admin"].includes(user?.role);
  const [candidates, setCandidates] = useState([]);
  const [stages, setStages] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filterBoutique, setFilterBoutique] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState(null);
  const closeCandidate = useCallback(() => setOpenId(null), []);
  const [showInsights, setShowInsights] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsMonths, setInsightsMonths] = useState(6);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeWorklist, setActiveWorklist] = useState("discover");
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [sort, setSort] = useState("recent");
  function openTool(event,show){const menu=event.currentTarget.closest('details');menu.open=false;menu.querySelector('summary').focus();show(true);}
  const canSeeInsights = ["owner", "coo", "recruiter", "onboarding_admin"].includes(user?.role);

  const loadInsights = useCallback(async (months) => {
    setInsightsLoading(true);
    try {
      const r = await api.get(`/hiring/analytics/rejections?months=${months}`);
      setInsights(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load insights");
    } finally { setInsightsLoading(false); }
  }, []);

  useEffect(() => {
    if (showInsights && canSeeInsights) loadInsights(insightsMonths);
  }, [showInsights, insightsMonths, canSeeInsights, loadInsights]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [c, s, d] = await Promise.all([
        api.get("/hiring/candidates"),
        api.get("/hiring/stages"),
        api.get("/hiring/dashboard"),
      ]);
      setCandidates(c.data.candidates || []);
      setStages(s.data.stages || []);
      setDashboard(d.data);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Could not load pipeline");
      toast.error(e?.response?.data?.detail || "Could not load pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return candidates.filter((c) => {
      if (filterBoutique !== "all") {
        const bc = c.boutique_code || "TBD";
        if (bc !== filterBoutique) return false;
      }
      if (filterRole !== "all" && c.role !== filterRole) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(`${c.name || ""} ${c.email || ""} ${c.source || ""}`.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [candidates, filterBoutique, filterRole, search]);

  const currentWorklist = WORKLIST_STAGES.find((group) => group.id === activeWorklist) || WORKLIST_STAGES[0];
  const worklistCandidates = useMemo(() => filtered.filter((candidate) => currentWorklist.stages.includes(candidate.stage)).sort((a,b)=>sort === "name" ? (a.name||"").localeCompare(b.name||"") : (Date.parse(b.created_at)||0)-(Date.parse(a.created_at)||0)), [filtered, currentWorklist, sort]);
  const selectedCandidate = worklistCandidates.find((candidate) => candidate.id === selectedCandidateId) || worklistCandidates[0] || null;
  const selectedStage = selectedCandidate ? stages.find((stage) => stage.key === selectedCandidate.stage) : null;
  const responsibility = selectedCandidate ? RESPONSIBILITY[selectedCandidate.stage] || { owner: "Hiring team", action: "Review candidate", evidence: [] } : null;

  return <main className="hiring-workspace hiring-workspace--compact" data-testid="hiring-workspace">
    <section className="hiring-workspace__vessel" aria-labelledby="hiring-title">
      <div className="toto-vessel-content-slot" data-vessel-content-slot>
      <header className="hiring-workspace__header">
        <h1 id="hiring-title">Hiring</h1>
        <time dateTime={new Date().toLocaleDateString('en-CA')}>{new Intl.DateTimeFormat('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'}).format(new Date())}</time>
      </header>
      <div className="hiring-directory__bar">
        <nav className="hiring-workspace__stages" aria-label="Hiring stages">
          {WORKLIST_STAGES.map(group=>{const count=filtered.filter(candidate=>group.stages.includes(candidate.stage)).length;return <button type="button" key={group.id} onClick={()=>setActiveWorklist(group.id)} aria-pressed={group.id===currentWorklist.id} className={group.id===currentWorklist.id?'is-active':''} data-testid={`hiring-stage-${group.id}`}><span><strong>{group.label}</strong><small>{loading?'Loading':`${count} candidates`}</small></span></button>;})}
        </nav>

      </div>
      <div className="hiring-workspace__content" role="region" aria-label="Hiring candidates and process" tabIndex={0}>
        <div className="hiring-workspace__toolbar">
          <label className="hiring-workspace__search"><Search size={20} aria-hidden="true"/><input type="search" aria-label="Search candidates" value={search} onChange={event=>setSearch(event.target.value)} placeholder="Search candidates…" data-testid="hiring-search"/></label>
          <div className="hiring-workspace__role-filter" role="group" aria-label="Filter candidates by role">{[['all','All roles'],['MT','MT'],['EG','EG']].map(([key,label])=><button key={key} onClick={()=>setFilterRole(key)} aria-pressed={filterRole===key} className={filterRole===key?'is-active':''} data-testid={`hiring-role-${key}`}>{label}</button>)}</div>
          {isLeadership&&<label className="hiring-workspace__boutique-filter"><span className="sr-only">Filter candidates by boutique</span><select value={filterBoutique} onChange={event=>setFilterBoutique(event.target.value)} data-testid="hiring-boutique-filter"><option value="all">All boutiques</option>{Array.from(new Set([...BOUTIQUES,...candidates.map(candidate=>candidate.boutique_code).filter(Boolean)])).map(code=><option key={code} value={code}>{boutiqueMeta[code]?.name||code}</option>)}<option value="TBD">Boutique pending</option></select></label>}
        <div className="hiring-directory__tools">
          {canSeeInsights&&<details className="hiring-directory__tool-menu"><summary>Tools</summary><div><button aria-label="Open hiring insights" onClick={event=>openTool(event,setShowInsights)}>Insights</button><button aria-label="Manage offer templates" onClick={event=>openTool(event,setShowTemplates)}>Offer templates</button></div></details>}
          {canSeeInsights&&<><button className="hiring-directory__desktop-tool" onClick={()=>setShowInsights(true)} aria-label="Open hiring insights" data-testid="hiring-insights">Insights</button><button className="hiring-directory__desktop-tool" onClick={()=>setShowTemplates(true)} aria-label="Manage offer templates">Templates</button></>}
          <button className="hiring-workspace__new-button" onClick={()=>setShowNew(true)} data-testid="hiring-new-candidate">New candidate</button>
        </div>
        </div>
        <section className="hiring-workspace__process" aria-labelledby="hiring-process-title" aria-busy={loading}>
          <h2 id="hiring-process-title" className="sr-only">{currentWorklist.label}</h2>
          {loadError?<div className="hiring-workspace__status" role="alert"><AlertTriangle size={20}/><span>{loadError}</span><button onClick={load}>Retry</button></div>:loading&&!candidates.length?<div className="hiring-workspace__status" role="status">Loading candidates…</div>:!selectedCandidate?<div className="hiring-directory__empty"><Binoculars size={36} aria-hidden="true"/><h2>No candidates in {currentWorklist.label}</h2><p>No candidates in {currentWorklist.label.toLowerCase()} match this view.</p><div><button className="hiring-workspace__new-button" onClick={()=>setShowNew(true)}>Add candidate</button><button onClick={()=>{setSearch('');setFilterRole('all');setFilterBoutique('all');}}>Clear filters</button></div></div>:<div className="hiring-directory__body">
            <section className="hiring-directory__list" aria-label="Candidates">
              <header><h2>Candidates ({worklistCandidates.length})</h2><label><span className="sr-only">Sort candidates</span><select value={sort} onChange={event=>setSort(event.target.value)}><option value="recent">Most recent</option><option value="name">Name</option></select></label></header>
              <div>{worklistCandidates.map(candidate=><button className={candidate.id===selectedCandidate.id?'is-selected':''} key={candidate.id} data-testid={`hiring-select-${candidate.id}`} aria-pressed={candidate.id===selectedCandidate.id} onClick={()=>{setSelectedCandidateId(candidate.id);if(window.matchMedia?.('(max-width: 700px)').matches)setOpenId(candidate.id);}}><span className="hiring-directory__initials" aria-hidden="true">{candidateInitials(candidate.name)}</span><span className="hiring-directory__row-copy"><strong>{candidate.name||'Unnamed candidate'}</strong><span>{candidate.role} · {boutiqueMeta[candidate.boutique_code]?.name||candidate.boutique_code||'Boutique pending'}</span><small>{stages.find(stage=>stage.key===candidate.stage)?.label||candidate.stage?.replaceAll('_',' ')}</small></span><ChevronRight size={18} aria-hidden="true"/></button>)}</div>
            </section>
            <section className="hiring-directory__detail" aria-label="Selected candidate">
              <header className="hiring-workspace__candidate-summary"><span className="hiring-directory__initials" aria-hidden="true">{candidateInitials(selectedCandidate.name)}</span><div><h2>{selectedCandidate.name}</h2><p>{selectedCandidate.role==='EG'?'Experience Guide':'Massage Therapist'} · {boutiqueMeta[selectedCandidate.boutique_code]?.name||selectedCandidate.boutique_code||'Boutique pending'}</p></div></header>
              <div className="hiring-directory__responsibility"><section className="hiring-workspace__next-action"><h3><ClipboardCheck size={20}/>Next action</h3><p>{responsibility.action}</p></section><section><h3><Users size={20}/>Owner</h3><p>{responsibility.owner}</p></section><section><h3><Clock size={20}/>In stage</h3><p>{dueLabel(selectedCandidate.stage_entered_at)}</p></section></div>
              <section className="hiring-workspace__handoff"><h3>Required handoff</h3><ul>{responsibility.evidence.map(item=><li key={item}><FileText size={18} aria-hidden="true"/><span>{item}</span></li>)}</ul></section>
              <button className="hiring-directory__open" onClick={()=>setOpenId(selectedCandidate.id)} data-testid={`candidate-card-${selectedCandidate.id}`}>Open candidate record</button>
            </section>
          </div>}
        </section>

      </div>
      </div>
    </section>
    {canSeeInsights && showInsights && <HiringInsightsDialog onClose={() => setShowInsights(false)}><InsightsPanel open onToggle={() => setShowInsights(false)} data={insights} loading={insightsLoading} months={insightsMonths} onChangeMonths={setInsightsMonths} onOpenCandidate={id => { setShowInsights(false); setOpenId(id); }} candidates={candidates} stages={stages} /></HiringInsightsDialog>}
    {showNew && <NewCandidateDialog onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    {showTemplates && <OfferTemplatesDialog onClose={() => setShowTemplates(false)} />}
    {openId && <CandidateDrawer candidateId={openId} stages={stages} onClose={closeCandidate} onChanged={load} />}
    <TotoAdaptiveDock active="workspace" destinations={[]} />
  </main>;
}

function HiringInsightsDialog({ children, onClose }) {
  const panel = useRef(null);
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    const trigger = document.activeElement;
    panel.current?.querySelector('button')?.focus();
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); close.current(); }
      if (event.key !== 'Tab') return;
      const controls = [...panel.current.querySelectorAll('button,a[href],input,select,[tabindex="0"]')].filter(element => !element.disabled && element.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.removeEventListener('keydown', keydown); if(trigger?.isConnected) trigger.focus(); };
  }, []);
  return <div className="hiring-workspace__insights-overlay" onClick={onClose}><div ref={panel} role="dialog" aria-modal="true" aria-label="Hiring insights" onClick={event => event.stopPropagation()}><button type="button" onClick={onClose} aria-label="Close hiring insights">Close <X size={18}/></button>{children}</div></div>;
}

// ────────── Candidate card ──────────
function CandidateCard({ candidate, muted, onOpen }) {
  const meta = candidate.boutique_code ? boutiqueMeta[candidate.boutique_code] : null;
  const days = daysSince(candidate.stage_entered_at);
  return (
    <button
      onClick={onOpen}
      data-testid={`candidate-card-${candidate.id}`}
      className={`w-full text-left bg-white subtle-border rounded-xl p-3 hover:shadow-sm transition-shadow ${muted ? "opacity-60" : ""}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-stone-900 truncate font-medium">{candidate.name}</div>
          <div className="flex items-center gap-2 mt-1 text-[10px] label-eyebrow text-stone-400">
            <span className="uppercase">{candidate.role}</span>
            <span>·</span>
            {meta ? (
              <span className="inline-flex items-center gap-1"><span className={`w-1 h-1 rounded-full ${meta.dot}`} />{candidate.boutique_code}</span>
            ) : (<span>TBD</span>)}
            {candidate.source && <><span>·</span><span className="truncate max-w-[70px]" title={candidate.source}>{candidate.source}</span></>}
          </div>
        </div>
        <ChevronRight size={13} strokeWidth={1.5} className="text-stone-300 shrink-0" />
      </div>
      {days !== null && (
        <div className="mt-2 flex items-center gap-1 text-[10px] text-stone-400">
          <Clock size={9} strokeWidth={1.5} />
          <span>{days === 0 ? "today" : `${days}d in stage`}</span>
        </div>
      )}
    </button>
  );
}

function Kpi({ label, value, tone = "neutral" }) {
  const toneCls = {
    neutral: "text-stone-900",
    success: "text-emerald-700",
    danger: "text-rose-600",
    muted: "text-stone-500",
  }[tone];
  return (
    <div className="bg-white subtle-border rounded-2xl p-4">
      <div className="text-[10px] label-eyebrow text-stone-400">{label}</div>
      <div className={`text-xl font-serif-display mt-1 tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}

// ────────── New Candidate Dialog ──────────
function NewCandidateDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    role: "MT", boutique_code: "", source: "", initial_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const body = { ...form, boutique_code: form.boutique_code || null };
      await api.post("/hiring/candidates", body);
      toast.success("Candidate created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="hiring-new-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center px-5 py-4 border-b border-stone-100">
          <div>
            <div className="text-[10px] label-eyebrow text-stone-400">Sheila's screen</div>
            <div className="font-serif-display text-lg text-stone-900">New candidate</div>
          </div>
          <button onClick={onClose} className="ml-auto p-1 text-stone-400 hover:text-stone-800"><X size={16} strokeWidth={1.5} /></button>
        </header>
        <div className="p-5 grid grid-cols-2 gap-4">
          <F label="First name"><input value={form.first_name} onChange={set("first_name")} data-testid="new-first" className="hi-input" /></F>
          <F label="Last name"><input value={form.last_name} onChange={set("last_name")} data-testid="new-last" className="hi-input" /></F>
          <F label="Email" full><input value={form.email} onChange={set("email")} data-testid="new-email" className="hi-input" /></F>
          <F label="Phone"><input value={form.phone} onChange={set("phone")} data-testid="new-phone" className="hi-input" /></F>
          <F label="Role">
            <select value={form.role} onChange={set("role")} data-testid="new-role" className="hi-input">
              <option value="MT">Massage Therapist (MT)</option>
              <option value="EG">Experience Guide (EG)</option>
            </select>
          </F>
          <F label="Boutique">
            <select value={form.boutique_code} onChange={set("boutique_code")} data-testid="new-boutique" className="hi-input">
              <option value="">TBD (unassigned)</option>
              {BOUTIQUES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </F>
          <F label="Source">
            <input value={form.source} onChange={set("source")} data-testid="new-source" className="hi-input" placeholder="Indeed, Referral, Walk-in…" />
          </F>
          <F label="Initial screening notes (optional)" full>
            <textarea rows={2} value={form.initial_notes} onChange={set("initial_notes")} data-testid="new-notes" className="hi-input resize-none" />
          </F>
        </div>
        <footer className="flex items-center px-5 py-3 border-t border-stone-100 bg-stone-50 rounded-b-2xl">
          <div className="text-[10px] label-eyebrow text-stone-500">Starts at stage · Applied</div>
          <button
            onClick={save}
            disabled={busy}
            data-testid="new-save"
            className="ml-auto px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Create"}
          </button>
        </footer>
      </div>
      <style>{`.hi-input{width:100%;padding:0.5rem 0.75rem;border-radius:0.5rem;border:1px solid #e7e5e4;background:#fff;font-size:0.875rem;color:#292524;outline:none}.hi-input:focus{border-color:#a8a29e}`}</style>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <label className={`${full ? "col-span-2" : ""} space-y-1 block`}>
      <span className="text-[10px] label-eyebrow text-stone-500">{label}</span>
      {children}
    </label>
  );
}

// ────────── Candidate Drawer (detail + stage advance) ──────────
function CandidateDrawer({ candidateId, stages, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const drawerRef = useRef(null);
  useEffect(() => {
    const trigger = document.activeElement;
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const controls = [...drawerRef.current.querySelectorAll('button,a[href],input,select,textarea,[tabindex="0"]')].filter(element => !element.disabled && element.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); drawerRef.current.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === drawerRef.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    drawerRef.current?.focus();
    return () => { document.removeEventListener('keydown', keydown); if (trigger?.isConnected) trigger.focus(); };
  }, [onClose]);
  const hadCandidate = useRef(false);
  useEffect(() => {
    if (c && !hadCandidate.current) drawerRef.current?.querySelector('[data-testid="drawer-close"]')?.focus();
    hadCandidate.current = Boolean(c);
  }, [c]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hiring/candidates/${candidateId}`);
      setC(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not found");
      onClose();
    }
  }, [candidateId, onClose]);

  useEffect(() => { load(); }, [load]);

  if (!c) {
    return (
      <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm" onClick={onClose}>
        <div ref={drawerRef} role="dialog" aria-modal="true" aria-label="Candidate record" tabIndex={-1} className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 space-y-4" data-testid="drawer-loading">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-3 w-32" />
            <div className="pt-4"><Skeleton className="h-24 w-full rounded-2xl" /></div>
            <Skeleton className="h-24 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  const advance = async (toStage, data) => {
    setBusy(true);
    try {
      const r = await api.post(`/hiring/candidates/${candidateId}/stage`, { to_stage: toStage, data });
      setC(r.data);
      onChanged();
      toast.success(`Moved to ${stages.find((s) => s.key === r.data.stage)?.label || r.data.stage}`);
      if (r.data.stage === "hired") celebrateSigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Advance failed");
    } finally { setBusy(false); }
  };

  const meta = c.boutique_code ? boutiqueMeta[c.boutique_code] : null;

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} data-testid="candidate-drawer">
      <div ref={drawerRef} role="dialog" aria-modal="true" aria-label="Candidate record" tabIndex={-1} className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white z-10 border-b border-stone-100 px-6 py-4 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10px] label-eyebrow text-stone-400">
              <span className="uppercase">{c.role}</span>
              <span>·</span>
              {meta ? <span className="inline-flex items-center gap-1"><span className={`w-1 h-1 rounded-full ${meta.dot}`} />{c.boutique_code}</span> : <span>TBD</span>}
              {c.source && <><span>·</span><span>{c.source}</span></>}
            </div>
            <div className="font-serif-display text-xl text-stone-900 break-words">{c.name}</div>
            <div className="text-xs text-stone-500">{c.email && <a href={`mailto:${c.email}`}>{c.email}</a>} {c.phone && <a href={`tel:${c.phone}`}> · {c.phone}</a>}</div>
          </div>
          <button onClick={onClose} aria-label="Close candidate record" data-testid="drawer-close" className="p-1 text-stone-400 hover:text-stone-800"><X size={16} strokeWidth={1.5} /></button>
        </header>

        <div className="p-6 space-y-6">
          {/* Stage timeline */}
          <StageTimeline candidate={c} stages={stages} />

          {/* Current-stage action panel */}
          <StagePanel candidate={c} stages={stages} onAdvance={advance} busy={busy} />

          {/* History */}
          <section>
            <div className="font-serif-display text-base text-stone-900 mb-2">History</div>
            <ul className="space-y-1 text-xs text-stone-500" data-testid="drawer-history">
              {[...(c.stage_history || [])].reverse().map((h, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className="text-[10px] text-stone-400 tabular-nums">{h.entered_at?.slice(0, 10)}</span>
                  <span className="text-stone-700">{(stages.find((s) => s.key === h.stage) || {}).label || h.stage}</span>
                  {h.entered_by_name && <span className="text-stone-400">· {h.entered_by_name}</span>}
                  {h.notes && <span className="text-stone-500 italic whitespace-pre-wrap break-words">— {h.notes}</span>}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function StageTimeline({ candidate, stages }) {
  const active = stages.filter((s) => s.key !== "not_hired");
  const idx = active.findIndex((s) => s.key === candidate.stage);
  const notHired = candidate.stage === "not_hired";
  return (
    <section data-testid="drawer-timeline">
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {active.map((s, i) => {
          const done = i < idx;
          const current = i === idx && !notHired;
          const isEGSkip = candidate.role === "EG" && s.key === "license_cleared";
          return (
            <React.Fragment key={s.key}>
              <div
                className={`px-3 py-1 rounded-full text-[10px] whitespace-nowrap ${
                  isEGSkip ? "bg-stone-100 text-stone-300 line-through"
                  : current ? "bg-stone-900 text-white"
                  : done ? "bg-emerald-100 text-emerald-700"
                  : "bg-stone-100 text-stone-400"
                }`}
              >
                {s.label}
              </div>
              {i < active.length - 1 && <span className="text-stone-300 text-[10px]">›</span>}
            </React.Fragment>
          );
        })}
        {notHired && (
          <div className="ml-3 px-3 py-1 rounded-full text-[10px] bg-rose-100 text-rose-700">Not hired</div>
        )}
      </div>
    </section>
  );
}

// ─── Per-stage action panels ───
function StagePanel({ candidate, stages, onAdvance, busy }) {
  const stage = candidate.stage;
  if (stage === "hired") {
    return (
      <section className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800" data-testid="stage-hired-panel">
        <div className="font-medium">🎉 Hired</div>
        <div className="text-xs mt-1">Employee record created — they will now appear in People · Directory and Onboarding will start their 30/60/90 check-ins.</div>
      </section>
    );
  }
  if (stage === "not_hired") {
    return (
      <section className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm text-rose-800" data-testid="stage-nothired-panel">
        <div className="font-medium">Closed as Not Hired</div>
        <div className="text-xs mt-1">{candidate.final?.not_hired_reason || "No reason recorded."}</div>
      </section>
    );
  }

  return (
    <section className="bg-white subtle-border rounded-2xl p-5" data-testid="stage-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[10px] label-eyebrow text-stone-500">Current stage</div>
        <div className="font-serif-display text-lg text-stone-900 flex-1">
          {(stages.find((s) => s.key === stage) || {}).label || stage}
        </div>
      </div>
      <StageForm candidate={candidate} onAdvance={onAdvance} busy={busy} />
    </section>
  );
}

function StageForm({ candidate, onAdvance, busy }) {
  const [data, setData] = useState({});
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const stage = candidate.stage;
  const isMT = candidate.role === "MT";

  const rejectBtn = (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        const reason = window.prompt("Reason for not hiring?") || "";
        if (!reason) return;
        onAdvance("not_hired", { not_hired_reason: reason });
      }}
      data-testid="stage-reject"
      className="px-3 py-1.5 rounded-full text-xs text-rose-700 hover:bg-rose-50 subtle-border border-rose-200"
    >
      Reject
    </button>
  );

  if (stage === "applied") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">Complete the initial phone / Zoom screening with the candidate.</p>
        <F label="Screening notes"><textarea rows={3} onChange={set("notes")} data-testid="ps-notes" className="hi-input resize-none" /></F>
        <F label="Availability confirmed"><input onChange={set("availability")} placeholder="Weekdays after 4pm, all weekends" data-testid="ps-availability" className="hi-input" /></F>
        <label className="flex items-center gap-2 text-sm text-stone-700"><input type="checkbox" onChange={set("understands_role")} data-testid="ps-understands" /> Candidate understands the {isMT ? "MT" : "EG"} role</label>
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance(null, data)} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Move to Phone Screen</button>
          {rejectBtn}
        </div>
      </div>
    );
  }
  if (stage === "phone_screen") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">Send the GM a calendar invite with candidate details + the editable conditional offer letter.</p>
        <F label="GM interview scheduled for"><input type="datetime-local" onChange={set("scheduled_at")} data-testid="ps-schedule" className="hi-input" /></F>
        <F label="Assigned GM (email)" full><input onChange={set("assigned_gm_email")} placeholder="damon.mitchell@rainbow.demo" data-testid="ps-gm" className="hi-input" /></F>
        <F label="Concerns / items to validate" full><textarea rows={2} onChange={set("gm_flags")} data-testid="ps-flags" className="hi-input resize-none" /></F>
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance(null, data)} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Send to GM</button>
          {rejectBtn}
        </div>
      </div>
    );
  }
  if (stage === "gm_interview") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">In-person interview + boutique tour. Target: within 48 hours of phone screen.</p>
        <F label="Interview notes" full><textarea rows={3} onChange={set("notes")} data-testid="gi-notes" className="hi-input resize-none" /></F>
        {isMT ? (
          <>
            <F label="Practical evaluation — technique & flow"><textarea rows={2} onChange={set("technique")} data-testid="gi-tech" className="hi-input resize-none" /></F>
          </>
        ) : (
          <F label="Warmth, polish, reliability, sales comfort"><textarea rows={2} onChange={set("polish")} data-testid="gi-polish" className="hi-input resize-none" /></F>
        )}
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance(null, data)} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Interview complete → Decision</button>
          {rejectBtn}
        </div>
      </div>
    );
  }
  if (stage === "hire_decision") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">GM decision. If hiring, extend the conditional offer letter on the spot.</p>
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance("conditional_offer", { decision: "hire" })} data-testid="stage-hire" className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-500">Hire → extend offer</button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              const reason = window.prompt("Reason for not hiring?") || "";
              if (!reason) return;
              onAdvance("not_hired", { decision: "no_hire", not_hired_reason: reason });
            }}
            data-testid="stage-reject"
            className="px-3 py-1.5 rounded-full text-xs text-rose-700 hover:bg-rose-50 subtle-border border-rose-200"
          >
            Do not hire
          </button>
        </div>
      </div>
    );
  }
  if (stage === "conditional_offer") {
    return <ConditionalOfferPanel candidate={candidate} onAdvance={onAdvance} busy={busy} rejectBtn={rejectBtn} />;
  }
  if (stage === "offer_signed") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">
          {isMT ? "MT license required before background check." : "Kick off background check via Sheila."}
        </p>
        {isMT && (
          <div className="grid grid-cols-2 gap-3">
            <F label="MT license #"><input onChange={set("license_number")} data-testid="ml-num" className="hi-input" /></F>
            <F label="Issuing state"><input onChange={set("license_state")} placeholder="FL" data-testid="ml-state" className="hi-input" /></F>
            <F label="License expiry"><input type="date" onChange={set("license_expiry_date")} data-testid="ml-exp" className="hi-input" /></F>
            <F label="License URL / photo"><input onChange={set("url")} placeholder="https://…" data-testid="ml-url" className="hi-input" /></F>
          </div>
        )}
        <F label="Background check provider" full><input onChange={set("provider")} placeholder="Checkr / Sterling / GoodHire" data-testid="bc-provider" className="hi-input" /></F>
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance("background_check", data)} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Kick off background check</button>
          {rejectBtn}
        </div>
      </div>
    );
  }
  if (stage === "background_check") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">Typical turnaround 3 business days. Confirm clear to GM + KK once returned.</p>
        <F label="Notes / provider status" full><input onChange={set("notes")} data-testid="bc-notes" className="hi-input" /></F>
        <div className="flex items-center gap-2 pt-2">
          {isMT ? (
            <button disabled={busy} onClick={() => onAdvance("license_cleared", { cleared_at: new Date().toISOString(), ...data })} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Cleared → verify license</button>
          ) : (
            <button disabled={busy} onClick={() => onAdvance("training_scheduled", { cleared_at: new Date().toISOString(), ...data })} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Cleared → schedule training</button>
          )}
          {rejectBtn}
        </div>
      </div>
    );
  }
  if (stage === "license_cleared") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">Verify MT license with the state board before training.</p>
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance("training_scheduled", { cleared_at: new Date().toISOString() })} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">License verified → schedule training</button>
          {rejectBtn}
        </div>
      </div>
    );
  }
  if (stage === "training_scheduled") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">GM sets training date. Sheila confirms background check the day before.</p>
        <div className="grid grid-cols-2 gap-3">
          <F label="Training date"><input type="date" onChange={set("scheduled_at")} data-testid="tr-date" className="hi-input" /></F>
          <F label="First shift"><input type="date" onChange={set("first_shift_at")} data-testid="tr-shift" className="hi-input" /></F>
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-700"><input type="checkbox" onChange={set("dress_code_sent")} data-testid="tr-dress" /> Dress code / uniform expectations sent</label>
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance("ready_to_train", data)} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Ready to train</button>
          {rejectBtn}
        </div>
      </div>
    );
  }
  if (stage === "ready_to_train") {
    return (
      <div className="space-y-3">
        <p className="text-xs text-stone-500">Confirming creates or links a personnel record after server validation. Login access is separate. Identity conflicts remain pending review in Hiring.</p>
        <div className="flex items-center gap-2 pt-2">
          <button disabled={busy} onClick={() => onAdvance("hired", {})} data-testid="stage-hire-final" className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-500">Mark hired</button>
          {rejectBtn}
        </div>
      </div>
    );
  }
  if(stage === "hired")return <div className="space-y-3"><p role="status">{candidate.personnel_handoff?.status==='ready'?'Personnel record linked. Login access remains separate.':'Hired · personnel record pending review or synchronization. Login access remains separate.'}</p>{candidate.personnel_handoff&&candidate.personnel_handoff.status!=='ready'&&<button disabled={busy} onClick={()=>onAdvance('hired',{})}>Retry personnel synchronization</button>}</div>;
  return <div className="text-sm text-stone-500">No actions for this stage.</div>;
}

// ─────────────────────────── Conditional Offer stage panel ───────────────────────────
function ConditionalOfferPanel({ candidate, onAdvance, busy, rejectBtn }) {
  const [form, setForm] = useState({
    pay_rate: (candidate.offer?.overrides?.pay_rate) || "",
    start_date: (candidate.offer?.overrides?.start_date) || "",
    employment_type: (candidate.offer?.overrides?.employment_type) || "Part-Time",
    director_name: (candidate.offer?.overrides?.director_name) || "Katrina Gell",
    deadline_days: (candidate.offer?.overrides?.deadline_days) || 3,
  });
  const [preview, setPreview] = useState(candidate.offer?.rendered_body || "");
  const [previewOpen, setPreviewOpen] = useState(!!candidate.offer?.rendered_body);
  const [sending, setSending] = useState(false);
  const [publicUrl, setPublicUrl] = useState(candidate.offer?.magic_token ? buildPublicUrl(candidate.offer.magic_token) : "");
  const status = candidate.offer?.status || "not_sent";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));

  const doPreview = async () => {
    try {
      const r = await api.post(`/hiring/candidates/${candidate.id}/offer/preview`, form);
      setPreview(r.data.rendered);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const doSend = async () => {
    if (candidate.role === "EG" && !form.pay_rate) return toast.error("Pay rate is required for EGs");
    if (!form.start_date) return toast.error("Start date is required");
    setSending(true);
    try {
      const r = await api.post(`/hiring/candidates/${candidate.id}/offer/send`, form);
      setPublicUrl(r.data.public_url);
      toast.success("Offer ready to share");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Link copied");
  };

  if (status === "signed") {
    return (
      <div className="space-y-3" data-testid="offer-signed-view">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 text-sm text-emerald-800">
          <Check size={14} strokeWidth={1.5} />
          Offer signed by <span className="font-medium">{candidate.offer?.signed_name}</span> on {(candidate.offer?.signed_at || "").slice(0, 10)}
        </div>
        <div className="flex items-center gap-2 pt-2">
          {candidate.role === "MT" ? (
            <button disabled={busy} onClick={() => onAdvance(null, {})} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Continue → license & background check</button>
          ) : (
            <button disabled={busy} onClick={() => onAdvance(null, {})} data-testid="stage-advance" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">Continue → background check</button>
          )}
          {rejectBtn}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="offer-flow">
      <p className="text-xs text-stone-500">Fill in the offer variables, preview the letter, and share the signature link — the candidate can sign live on your device or from their phone.</p>
      <div className="grid grid-cols-2 gap-3">
        <F label="Pay rate ($/hr for EG · e.g. 17.50)"><input value={form.pay_rate} onChange={set("pay_rate")} data-testid="co-rate" className="hi-input" placeholder="17.50" /></F>
        <F label="Target start date"><input value={form.start_date} onChange={set("start_date")} data-testid="co-start" className="hi-input" placeholder="March 3, 2026 or TBD in June" /></F>
        <F label="Employment type">
          <select value={form.employment_type} onChange={set("employment_type")} data-testid="co-type" className="hi-input">
            <option>Part-Time</option>
            <option>Full-Time</option>
          </select>
        </F>
        <F label="Signing deadline (days)"><input type="number" min="1" max="30" value={form.deadline_days} onChange={set("deadline_days")} data-testid="co-deadline" className="hi-input" /></F>
        <F label="Director name (for at-will clause)" full><input value={form.director_name} onChange={set("director_name")} data-testid="co-director" className="hi-input" /></F>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button type="button" onClick={doPreview} data-testid="co-preview" className="text-xs px-3 py-1.5 rounded-full subtle-border text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1.5">
          <FileText size={12} strokeWidth={1.5} /> Preview
        </button>
        <button
          type="button"
          onClick={doSend}
          disabled={sending || (candidate.role === "EG" && !form.pay_rate) || !form.start_date}
          data-testid="co-send"
          className="text-xs px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send size={12} strokeWidth={1.5} /> {sending ? "Preparing…" : status === "sent" ? "Regenerate link" : "Send for signature"}
        </button>
      </div>

      {publicUrl && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 space-y-2" data-testid="co-link-panel">
          <div className="text-[10px] label-eyebrow text-stone-500">Signature link · valid 14 days</div>
          <div className="flex items-center gap-2">
            <input value={publicUrl} readOnly data-testid="co-link" className="flex-1 hi-input font-mono text-[11px] bg-white" onClick={(e) => e.target.select()} />
            <button onClick={copyLink} data-testid="co-copy" className="text-xs px-3 py-1.5 rounded-full bg-white subtle-border text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1.5">
              <Copy size={12} strokeWidth={1.5} /> Copy
            </button>
            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="co-open" className="text-xs px-3 py-1.5 rounded-full bg-white subtle-border text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1.5">
              Open
            </a>
          </div>
          <div className="text-[11px] text-stone-500">
            Hand the tablet to the candidate now, or send this link via email / text. Once they sign it, the pipeline advances automatically.
          </div>
        </div>
      )}

      {previewOpen && preview && (
        <div className="mt-3 border border-stone-200 rounded-xl overflow-hidden" data-testid="co-preview-panel">
          <div className="flex items-center px-3 py-2 border-b border-stone-100 bg-stone-50">
            <div className="text-[10px] label-eyebrow text-stone-500">Preview</div>
            <button onClick={() => setPreviewOpen(false)} className="ml-auto p-1 text-stone-400 hover:text-stone-800"><X size={13} strokeWidth={1.5} /></button>
          </div>
          <div className="p-5 max-h-[500px] overflow-y-auto offer-preview">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview}</ReactMarkdown>
          </div>
        </div>
      )}
      <style>{`
        .offer-preview h3 { font-family: var(--font-serif, Georgia, serif); font-size: 1rem; color: #1c1917; margin-top: 1rem; margin-bottom: 0.35rem; }
        .offer-preview p { color: #44403c; font-size: 0.82rem; line-height: 1.55; margin: 0.45rem 0; }
        .offer-preview strong { color: #1c1917; }
        .offer-preview em { color: #57534e; }
        .offer-preview ul, .offer-preview ol { padding-left: 1.25rem; font-size: 0.82rem; color: #44403c; margin: 0.3rem 0; }
        .offer-preview table { border-collapse: collapse; margin: 0.5rem 0; font-size: 0.72rem; }
        .offer-preview th, .offer-preview td { border: 1px solid #e7e5e4; padding: 0.25rem 0.5rem; text-align: left; }
        .offer-preview th { background: #fafaf9; font-weight: 500; color: #292524; }
      `}</style>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (!window.confirm("Mark as signed manually (skip candidate sign-off)?")) return;
            onAdvance(null, { manual_override: true });
          }}
          data-testid="co-manual"
          className="text-xs text-stone-500 hover:text-stone-800 underline"
        >
          Signed on paper — mark manually
        </button>
        {rejectBtn}
      </div>
    </div>
  );
}

function buildPublicUrl(token) {
  const origin = window.location.origin;
  return `${origin}/offer/${token}`;
}

// ─────────────────────────── Rejection insights panel ───────────────────────────
const STAGE_LABELS = {
  applied: "Applied",
  phone_screen: "Phone Screen",
  gm_interview: "GM Interview",
  hire_decision: "Hire Decision",
  conditional_offer: "Conditional Offer",
  offer_signed: "Offer Signed",
  background_check: "Background Check",
  license_cleared: "License Cleared",
  training_scheduled: "Training Scheduled",
  ready_to_train: "Ready to Train",
};

const THEME_TINT = {
  no_show: "bg-stone-100 text-stone-700 border-stone-200",
  availability: "bg-amber-50 text-amber-800 border-amber-200",
  skills_gap: "bg-sky-50 text-sky-800 border-sky-200",
  red_flags: "bg-rose-50 text-rose-800 border-rose-200",
  culture_fit: "bg-violet-50 text-violet-800 border-violet-200",
  compensation: "bg-emerald-50 text-emerald-800 border-emerald-200",
  unspecified: "bg-stone-50 text-stone-500 border-stone-200 italic",
  other: "bg-stone-50 text-stone-600 border-stone-200",
};

function InsightsPanel({ open, onToggle, data, loading, months, onChangeMonths, onOpenCandidate, candidates, stages }) {
  const topTheme = data?.themes?.[0];
  const summary = topTheme
    ? `${topTheme.count} of ${data.total_rejections} · ${topTheme.pct}% · ${topTheme.label}`
    : (data ? `${data?.total_rejections || 0} rejections in last ${months} months` : "");

  return (
    <section className="bg-white subtle-border rounded-2xl overflow-hidden" data-testid="hiring-insights">
      <button
        type="button"
        onClick={onToggle}
        data-testid="hiring-insights-toggle"
        className="w-full flex items-center gap-3 px-5 py-3 hover:bg-stone-50 transition-colors"
      >
        <Sparkles size={14} strokeWidth={1.5} className="text-stone-400" />
        <div className="font-serif-display text-stone-900 text-base flex-1 text-left">Why we didn't hire</div>
        {data && (
          <div className="text-xs text-stone-500 hidden md:block">
            Top: <span className="text-stone-800">{summary}</span>
          </div>
        )}
        <ChevronDown size={13} strokeWidth={1.5} className={`text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-stone-100 pt-4">
          {/* Timeframe selector */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] label-eyebrow text-stone-400">Window</span>
            {[3, 6, 12].map((m) => (
              <button
                key={m}
                onClick={() => onChangeMonths(m)}
                data-testid={`insights-window-${m}`}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  months === m ? "bg-stone-900 text-white" : "text-stone-500 bg-stone-100 hover:bg-stone-200"
                }`}
              >
                Last {m} mo
              </button>
            ))}
            <div className="ml-auto text-xs text-stone-400">
              {loading ? "Loading…" : data ? `${data.total_rejections} rejection${data.total_rejections === 1 ? "" : "s"}` : ""}
            </div>
          </div>

          {!data || data.total_rejections === 0 ? (
            <div className="text-sm text-stone-400 py-6 text-center">No rejections in this window — nicely done.</div>
          ) : (
            <>
              {/* Themes */}
              <div>
                <div className="text-[10px] label-eyebrow text-stone-500 mb-2">Themes</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="insights-themes">
                  {data.themes.map((t) => (
                    <div key={t.key} className={`rounded-2xl border p-4 ${THEME_TINT[t.key] || THEME_TINT.other}`} data-testid={`theme-${t.key}`}>
                      <div className="flex items-baseline gap-2">
                        <div className="text-sm font-medium">{t.label}</div>
                        <div className="ml-auto text-2xl font-serif-display tabular-nums">{t.count}</div>
                      </div>
                      <div className="mt-1 h-1 rounded-full bg-white/60 overflow-hidden">
                        <div className="h-full bg-current opacity-60" style={{ width: `${t.pct}%` }} />
                      </div>
                      <div className="mt-1 text-[10px] opacity-70">{t.pct}% of total</div>
                      {t.examples.length > 0 && (
                        <ul className="mt-2 space-y-1 text-[11px]">
                          {t.examples.map((ex, i) => (
                            <li key={i} className="truncate" title={ex.reason}>
                              <span className="font-medium">{ex.name}</span>{ex.boutique_code ? ` · ${ex.boutique_code}` : ""} — <span className="opacity-80">{ex.reason}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Stage dropoff + Source performance side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-stone-50 rounded-2xl p-4" data-testid="insights-dropoff">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 size={13} strokeWidth={1.5} className="text-stone-500" />
                    <div className="text-[10px] label-eyebrow text-stone-500">Where they dropped off</div>
                  </div>
                  {data.stage_dropoff.length === 0 ? (
                    <div className="text-xs text-stone-400">No data</div>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {data.stage_dropoff.map((r) => {
                        const label = STAGE_LABELS[r.stage] || (stages.find((s) => s.key === r.stage) || {}).label || r.stage;
                        const max = Math.max(...data.stage_dropoff.map((x) => x.count), 1);
                        return (
                          <li key={r.stage} className="flex items-center gap-3">
                            <div className="text-stone-700 w-40 shrink-0 truncate">{label}</div>
                            <div className="flex-1 h-2 rounded-full bg-stone-200 overflow-hidden">
                              <div className="h-full bg-stone-600" style={{ width: `${(r.count / max) * 100}%` }} />
                            </div>
                            <div className="text-stone-800 tabular-nums text-xs w-6 text-right">{r.count}</div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="bg-stone-50 rounded-2xl p-4" data-testid="insights-sources">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 size={13} strokeWidth={1.5} className="text-stone-500" />
                    <div className="text-[10px] label-eyebrow text-stone-500">Source performance</div>
                  </div>
                  {data.sources.length === 0 ? (
                    <div className="text-xs text-stone-400">No data</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-stone-400 text-[10px] uppercase">
                        <tr>
                          <th className="text-left py-1">Source</th>
                          <th className="text-right py-1">Applied</th>
                          <th className="text-right py-1">Hired</th>
                          <th className="text-right py-1">Rejected</th>
                          <th className="text-right py-1">Rej. %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.sources.slice(0, 6).map((r) => (
                          <tr key={r.source} className="border-t border-stone-200">
                            <td className="py-1.5 text-stone-800 truncate max-w-[120px]" title={r.source}>{r.source}</td>
                            <td className="py-1.5 text-right text-stone-600 tabular-nums">{r.total}</td>
                            <td className="py-1.5 text-right text-emerald-700 tabular-nums">{r.hired}</td>
                            <td className="py-1.5 text-right text-rose-700 tabular-nums">{r.not_hired}</td>
                            <td className={`py-1.5 text-right tabular-nums font-medium ${r.rejection_rate >= 70 ? "text-rose-600" : r.rejection_rate >= 40 ? "text-amber-600" : "text-stone-800"}`}>
                              {r.rejection_rate}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
